// V9.25 · Projeção de caixa 30/60/90 dias (a receber − a pagar sobre o saldo de hoje)
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, RefreshCw, Wallet } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const dia = (d) => (d || '').slice(8, 10) + '/' + (d || '').slice(5, 7);

export const ProjecaoTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/fin/ledger/cash-forecast');
      setData(d);
    } catch (_) { toast.error('Erro ao carregar a projeção de caixa.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const alerta = data?.alerta;
  const saldos = (data?.semanas || []).map((s) => s.saldo_acumulado);
  const minS = Math.min(0, ...saldos);
  const maxS = Math.max(1, ...saldos);
  const faixa = maxS - minS || 1;

  return (
    <div className="space-y-3" data-testid="tab-projecao">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Saldo de hoje somado ao que entra e sai de verdade nos próximos 90 dias
        </p>
        <Button variant="outline" size="sm" onClick={load} data-testid="proj-atualizar">
          <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
        </Button>
      </div>

      {alerta && (
        <Card className={alerta.nivel === 'danger' ? 'border-rose-300 bg-rose-50/70'
          : alerta.nivel === 'warn' ? 'border-amber-300 bg-amber-50/70'
            : 'border-emerald-300 bg-emerald-50/70'} data-testid="proj-alerta">
          <CardContent className="p-4 flex items-start gap-3">
            {alerta.nivel === 'ok'
              ? <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />
              : <AlertTriangle className={`w-5 h-5 shrink-0 ${alerta.nivel === 'danger' ? 'text-rose-700' : 'text-amber-700'}`} />}
            <p className="text-sm font-medium">{alerta.mensagem}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-xl border p-4 bg-foreground/5" data-testid="proj-saldo-hoje">
          <p className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-1">
            <Wallet className="w-3.5 h-3.5" /> Saldo hoje
          </p>
          <p className="text-2xl font-bold tabular-nums">{money(data?.saldo_hoje)}</p>
          <p className="text-xs text-muted-foreground">
            {data?.qtd_entradas || 0} entradas · {data?.qtd_saidas || 0} saídas previstas
          </p>
        </div>
        {(data?.janelas || []).map((j) => (
          <div key={j.dias} data-testid={`proj-janela-${j.dias}`}
            className={`rounded-xl border p-4 transition-transform hover:-translate-y-0.5 ${
              j.negativo ? 'border-rose-300 bg-rose-50/60' : 'border-emerald-300 bg-emerald-50/40'}`}>
            <p className="text-xs font-bold uppercase text-muted-foreground">Em {j.dias} dias</p>
            <p className={`text-2xl font-bold tabular-nums ${j.negativo ? 'text-rose-700' : ''}`}>
              {money(j.saldo_projetado)}
            </p>
            <p className="text-xs text-muted-foreground">
              + {money(j.entradas)} · − {money(j.saidas)}
            </p>
          </div>
        ))}
      </div>

      {!!(data?.em_atraso?.a_receber || data?.em_atraso?.a_pagar) && (
        <Card className="border-amber-300 bg-amber-50/50" data-testid="proj-atraso">
          <CardContent className="p-4 text-sm flex flex-wrap gap-x-6 gap-y-1">
            <span className="font-medium">Fora da projeção (já em atraso):</span>
            <span>A receber vencido: <strong className="tabular-nums">{money(data.em_atraso.a_receber)}</strong></span>
            <span>A pagar vencido: <strong className="tabular-nums">{money(data.em_atraso.a_pagar)}</strong></span>
            <span className="text-muted-foreground text-xs">
              Não entram no cálculo porque a data já passou — cobre ou renegocie para virarem caixa.
            </span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Semana a semana (13 semanas)</CardTitle></CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-muted-foreground">Carregando…</p>}
          <div className="flex items-end gap-1.5 h-44">
            {(data?.semanas || []).map((s) => {
              const alturaPct = ((s.saldo_acumulado - minS) / faixa) * 100;
              const neg = s.saldo_acumulado < 0;
              return (
                <div key={s.semana} className="flex-1 flex flex-col items-center justify-end gap-1 group"
                  data-testid={`proj-semana-${s.semana}`} title={`${s.inicio} → ${s.fim}`}>
                  <span className="text-[9px] tabular-nums opacity-0 group-hover:opacity-100 transition-opacity">
                    {money(s.saldo_acumulado)}
                  </span>
                  <div className={`w-full rounded-t transition-all ${neg ? 'bg-rose-500' : 'bg-emerald-500'}`}
                    style={{ height: `${Math.max(3, alturaPct)}%` }} />
                  <span className="text-[9px] text-muted-foreground">{dia(s.inicio)}</span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {(data?.semanas || []).slice(0, 8).map((s) => (
              <div key={s.semana} className="rounded border px-2 py-1.5 text-xs">
                <p className="font-medium">Sem. {s.semana} · {dia(s.inicio)}</p>
                <p className="text-emerald-700 tabular-nums">+ {money(s.entradas)}</p>
                <p className="text-rose-700 tabular-nums">− {money(s.saidas)}</p>
                <p className={`font-bold tabular-nums ${s.saldo_acumulado < 0 ? 'text-rose-700' : ''}`}>
                  = {money(s.saldo_acumulado)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjecaoTab;
